import React, { useEffect, useState } from "react";
import { createCustomer, updateCustomer } from "../../services/customerService";
import { errorAlert, successAlert } from "../common/Swal";

const BRAND = "#a87506";

function AddCustomerModal({ show, onClose, onSave, customer, isEdit = false }) {
  const [formData, setFormData] = useState({
    custName: "",
    phone1: "",
    phone2: "",
    cnic: "",
    address: "",
    openingBalance: "",
    openingGoldWeight: "",
    openingSilverWeight: "",
    refineCharges: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showOpening, setShowOpening] = useState(false);

  useEffect(() => {
    if (isEdit && customer) {
      setFormData({
        custName: customer.name || "",
        phone1: customer.phone || "",
        phone2: customer.phone2 || "",
        cnic: customer.cnic || "",
        address: customer.address || "",
        openingBalance: customer?.openingBalance || 0,
        openingGoldWeight: customer?.openingGoldWeight || 0,
        openingSilverWeight: customer?.openingSilverWeight || 0,
        refineCharges: customer?.setting?.refineCharges,
      });
    }
  }, [customer, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.custName.trim()) errs.custName = "Name is required";
    if (!formData.phone1.trim()) errs.phone1 = "Phone 1 is required";
    else if (!/^[0-9]{10,15}$/.test(formData.phone1))
      errs.phone1 = "Must be 10–15 digits";
    if (formData.phone2 && !/^[0-9]{10,15}$/.test(formData.phone2))
      errs.phone2 = "Must be 10–15 digits";
    if (formData.cnic && !/^[0-9]{13}$/.test(formData.cnic))
      errs.cnic = "Must be 13 digits";
    ["openingBalance", "openingGoldWeight", "openingSilverWeight"].forEach(
      (f) => {
        if (formData[f] !== "" && isNaN(Number(formData[f])))
          errs[f] = "Must be a valid number";
      },
    );
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const toNum = (val) => (val !== "" ? Number(val) : 0);

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      // const goldW = toNum(formData.openingGoldWeight);
      // const silverW = toNum(formData.openingSilverWeight);
      let response;

      if (isEdit) {
        response = await updateCustomer(customer._id, {
          name: formData.custName,
          phone: formData.phone1,
          phone2: formData.phone2,
          cnic: formData.cnic,
          address: formData.address,

          // opening snapshot
          openingBalance: toNum(formData.openingBalance),
          openingGoldWeight: toNum(formData.openingGoldWeight),
          openingSilverWeight: toNum(formData.openingSilverWeight),

          // Settings
          setting: { refineCharges: toNum(formData.refineCharges) },
        });
      } else {
        response = await createCustomer({
          name: formData.custName,
          phone: formData.phone1,
          phone2: formData.phone2,
          cnic: formData.cnic,
          address: formData.address,

          // opening snapshot
          openingBalance: toNum(formData.openingBalance),
          openingGoldWeight: toNum(formData.openingGoldWeight),
          openingSilverWeight: toNum(formData.openingSilverWeight),
          // Setting
          setting: { refineCharges: toNum(formData.refineCharges) },
        });
      }

      if (response.success) {
        setFormData({
          custName: "",
          phone1: "",
          phone2: "",
          cnic: "",
          address: "",
          openingBalance: "",
          openingGoldWeight: "",
          openingSilverWeight: "",
          refineCharges: "",
        });
        setShowOpening(false);
        onSave?.(response.customer);

        // Show message
        successAlert(
          `Customer ${isEdit ? "updated" : "created"} successfully!`,
        );

        onClose?.();
      } else {
        errorAlert(
          response.message ||
            `Failed to ${isEdit ? "update" : "create"} customer. Please try again.`,
        );
      }
    } catch (err) {
      errorAlert(
        err.response?.data?.message || err.message || "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleOpening = () => {
    setShowOpening((p) => {
      if (p)
        setFormData((f) => ({
          ...f,
          openingBalance: "",
          openingGoldWeight: "",
          openingSilverWeight: "",
        }));
      return !p;
    });
  };

  if (!show) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* ── Header ── */}
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={styles.avatarIcon}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <span style={{ color: "#fff", fontWeight: 500, fontSize: 15 }}>
              {isEdit ? "Edit" : "Add"} customer
            </span>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {/* ── Body ── */}
        <div style={styles.body}>
          {/* Customer name */}
          <Field label="Customer name *" error={errors.custName}>
            <input
              style={inputStyle(errors.custName)}
              name="custName"
              placeholder="e.g. Ahmed Khan"
              value={formData.custName}
              onChange={handleChange}
            />
          </Field>

          {/* Phone row */}
          <div style={styles.row}>
            <Field label="Phone 1 *" error={errors.phone1}>
              <input
                style={inputStyle(errors.phone1)}
                name="phone1"
                placeholder="03001234567"
                value={formData.phone1}
                onChange={handleChange}
              />
            </Field>
            <Field label="Phone 2" error={errors.phone2}>
              <input
                style={inputStyle(errors.phone2)}
                name="phone2"
                placeholder="Optional"
                value={formData.phone2}
                onChange={handleChange}
              />
            </Field>
          </div>

          {/* CNIC + Address row */}
          <div style={styles.row}>
            <Field label="CNIC" error={errors.cnic}>
              <input
                style={inputStyle(errors.cnic)}
                name="cnic"
                placeholder="13 digits"
                value={formData.cnic}
                onChange={handleChange}
              />
            </Field>
            <Field label="Address">
              <input
                style={inputStyle()}
                name="address"
                placeholder="City / Area"
                value={formData.address}
                onChange={handleChange}
              />
            </Field>
          </div>

          {/* Opening balance toggle */}
          <button style={styles.dividerBtn} onClick={toggleOpening}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerLabel}>
              <span style={styles.plusIcon}>
                {showOpening ? (
                  <i className="fas fa-minus fa-xs"></i>
                ) : (
                  <i className="fas fa-plus fa-xs"></i>
                )}
              </span>
              {isEdit ? "Edit" : "Add"} opening balance
            </span>
            <div style={styles.dividerLine} />
          </button>

          {/* Opening balance fields — CSS transition via inline max-height trick */}
          <div
            style={{
              overflow: "hidden",
              maxHeight: showOpening ? 200 : 0,
              opacity: showOpening ? 1 : 0,
              transition:
                "max-height 0.32s cubic-bezier(0.22,1,0.36,1), opacity 0.22s ease",
            }}
          >
            <div style={styles.openingInner}>
              <p style={styles.openingTitle}>Opening balance</p>
              <div style={styles.row}>
                <Field label="Balance (PKR)">
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    style={inputStyle()}
                    name="openingBalance"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    value={formData.openingBalance}
                    onChange={handleChange}
                  />
                </Field>
                <Field label="Gold weight (g)">
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    style={inputStyle()}
                    name="openingGoldWeight"
                    placeholder="0.000"
                    min="0"
                    step="0.001"
                    value={formData.openingGoldWeight}
                    onChange={handleChange}
                  />
                </Field>
              </div>
              <div style={{ marginTop: 8, maxWidth: "48%" }}>
                <Field label="Silver weight (g)">
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    style={inputStyle()}
                    name="openingSilverWeight"
                    placeholder="0.000"
                    min="0"
                    step="0.001"
                    value={formData.openingSilverWeight}
                    onChange={handleChange}
                  />
                </Field>
              </div>
              <span
                className="text-muted "
                style={{
                  fontSize: "12px",
                }}
              >
                <span className="text-danger">Negative:</span> Customer owes you
                |<span className="text-success">Positive:</span> You owe
                customer
              </span>
            </div>
          </div>

          {/* Seetting */}
          <div>
            <div style={styles.row}>
              <Field label="Refine Charges" error={errors.refineCharges}>
                <input
                  style={inputStyle(errors.refineCharges)}
                  name="refineCharges"
                  placeholder="0.00"
                  value={formData.refineCharges}
                  onChange={handleChange}
                />
              </Field>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{ ...styles.saveBtn, opacity: loading ? 0.65 : 1 }}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : isEdit ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-component ── */
function Field({ label, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
      <span
        style={{
          fontSize: 11,
          color: "#888",
          fontWeight: 500,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </span>
      {children}
      {error && <span style={{ fontSize: 11, color: "#e24b4a" }}>{error}</span>}
    </div>
  );
}

/* ── Styles ── */
const inputStyle = (err) => ({
  height: 36,
  padding: "0 10px",
  fontSize: 13,
  width: "100%",
  background: "#f5f5f5",
  border: `0.5px solid ${err ? "#e24b4a" : "#ddd"}`,
  borderRadius: 8,
  outline: "none",
  color: "#222",
});

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    overflow: "hidden",
    animation: "slideUp 0.28s cubic-bezier(0.22,1,0.36,1) both",
    marginTop: "10px",
  },
  header: {
    padding: "0.95rem 1.25rem",
    backgroundColor: BRAND,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  avatarIcon: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: "0.5px solid rgba(255,255,255,0.35)",
    background: "transparent",
    cursor: "pointer",
    color: "#fff",
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  body: {
    padding: "1.1rem 1.25rem",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  footer: {
    padding: "0.75rem 1.25rem 1rem",
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    borderTop: "0.5px solid #eee",
  },
  cancelBtn: {
    height: 34,
    padding: "0 14px",
    fontSize: 13,
    border: "0.5px solid #ddd",
    borderRadius: 8,
    background: "transparent",
    color: "#555",
    cursor: "pointer",
  },
  saveBtn: {
    height: 34,
    padding: "0 18px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 8,
    background: BRAND,
    color: "#fff",
    cursor: "pointer",
  },
  dividerBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px 0",
    margin: "4px 0 0",
    width: "100%",
  },
  dividerLine: { flex: 1, height: "0.5px", background: "#e5e5e5" },
  dividerLabel: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    color: "#888",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  plusIcon: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "0.5px solid #ccc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    lineHeight: 1,
  },
  openingInner: {
    background: "#f9f9f9",
    border: "0.5px solid #e5e5e5",
    borderRadius: 8,
    padding: "10px 12px 12px",
    marginBottom: 4,
  },
  openingTitle: {
    fontSize: 11,
    fontWeight: 500,
    color: "#888",
    marginBottom: 10,
    letterSpacing: "0.03em",
  },
};

export default AddCustomerModal;
